import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterPanel from '../FilterPanel/FilterPanel';
import EnhancedDataTable from '../EnhancedDataTable/EnhancedDataTable';
import EMSATabsContainer from './EMSATabsContainer';
import './EMSATrainingPrograms.css';

// URL mapping for navigation
const TAB_ROUTES = [
  // Page 1: EMS Training Programs
  [
    '/continuing-education',     // Continuing Education
    '/emspersonnel',            // EMS Personnel  
    '/public-safety-first-aid'  // Public Safety First Aid
  ],
  // Page 2: General Training Programs
  [
    '/epinephrine-auto-injector',    // Epinephrine Auto-Injector
    '/childcare-cpr-first-aid',      // Child Care Provider CPR and First Aid
    '/childcare-preventive-health',  // Child Care Provider Preventive Health & Safety
    '/schoolbusdriver'               // School Bus Driver First Aid
  ]
];

/**
 * @summary Main component for displaying EMSA Training Programs data
 * @param {number} initialPage - Initial page to display (1 or 2)
 * @param {number} initialTab - Initial tab index to display
 * @returns {React.ReactElement} Returns the EMSATrainingPrograms component
 */
const EMSATrainingPrograms = ({ initialPage = 1, initialTab = 0 }) => {
  const navigate = useNavigate();
  
  // Phone number formatting utility
  const formatPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return phone;
    
    // Extract only digits
    const digits = phone.replace(/\D/g, '');
    
    // If exactly 10 digits, format as (xxx) xxx-xxxx
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Return original value if not 10 digits
    return phone;
  };
  
  // Initialize state variables
  const [continuingEducation, setContinuingEducation] = useState([]);
  const [emsPersonnel, setEmsPersonnel] = useState([]);
  const [epinephrine, setEpinephrine] = useState([]);
  const [publicSafety, setPublicSafety] = useState([]);
  const [childCareProvider, setChildCareProvider] = useState([]);
  const [childCareProviderPreventiveHealth, setChildCareProviderPreventiveHealth] = useState([]);
  const [schoolBusDriverFirstAid, setSchoolBusDriverFirstAid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentTab, setCurrentTab] = useState(initialTab);

  // Path to data directory
  const dataPath = useMemo(() => {
    // Use process.env.PUBLIC_URL to get the correct base path
    return `${process.env.PUBLIC_URL}/data`;
  }, []);

  // Function to parse CSV data with proper handling of multi-line quoted fields
  const parseCSV = useCallback((csvText) => {
    if (!csvText || typeof csvText !== 'string') {
      console.error('Invalid CSV text:', csvText);
      return [];
    }

    try {
      // Remove BOM character if present
      const cleanText = csvText.replace(/^\ufeff/, '');

      // Parse CSV into rows, respecting quotes and newlines within quoted fields
      const parseCSVRows = (text) => {
        const rows = [];
        let currentRow = '';
        let inQuote = false;
        let prevChar = '';

        for (let i = 0; i < text.length; i++) {
          const char = text[i];

          // Handle quote characters
          if (char === '"') {
            // Check if this is an escaped quote (double quote)
            if (inQuote && text[i + 1] === '"') {
              currentRow += '"';
              i++; // Skip the next quote
            } else {
              inQuote = !inQuote;
            }
            currentRow += char;
          } else if (char === '\n' && !inQuote) {
            // End of row (only if not inside quotes)
            if (currentRow.trim()) {
              rows.push(currentRow);
            }
            currentRow = '';
          } else if (char === '\r' && !inQuote) {
            // Handle Windows line endings (CRLF)
            // Skip the \r, the \n will be processed next
            continue;
          } else {
            currentRow += char;
          }

          prevChar = char;
        }

        // Add the last row if not empty
        if (currentRow.trim()) {
          rows.push(currentRow);
        }

        return rows;
      };

      const rows = parseCSVRows(cleanText);

      if (rows.length === 0) {
        console.error('No rows found in CSV');
        return [];
      }

      // Parse a single CSV row into values
      const parseCSVRow = (row) => {
        const values = [];
        let currentValue = '';
        let inQuote = false;

        for (let i = 0; i < row.length; i++) {
          const char = row[i];

          if (char === '"') {
            // Check if this is an escaped quote (double quote)
            if (inQuote && row[i + 1] === '"') {
              currentValue += '"';
              i++; // Skip the next quote
            } else {
              inQuote = !inQuote;
            }
          } else if (char === ',' && !inQuote) {
            // End of value
            values.push(currentValue);
            currentValue = '';
          } else {
            currentValue += char;
          }
        }

        // Add the last value
        values.push(currentValue);

        // Clean up values: remove surrounding quotes and trim whitespace
        return values.map(v => {
          let cleaned = v.trim();
          // Remove surrounding quotes if present
          if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
            cleaned = cleaned.substring(1, cleaned.length - 1);
          }
          // Replace newlines with spaces for better display in tables
          cleaned = cleaned.replace(/\n/g, ' ').replace(/\r/g, '').trim();
          return cleaned;
        });
      };

      // Parse headers
      const headers = parseCSVRow(rows[0]);

      if (headers.length === 0) {
        console.error('No headers found in CSV');
        return [];
      }

      // Parse data rows
      const result = [];

      for (let i = 1; i < rows.length; i++) {
        const values = parseCSVRow(rows[i]);

        // Create object with headers as keys
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = index < values.length ? values[index] : '';
        });

        result.push(obj);
      }

      return result;
    } catch (err) {
      console.error('Error parsing CSV:', err);
      return [];
    }
  }, []);

  // Function to load data from CSV files
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // console.log('Loading data from:', dataPath);
      
      // Use dummy data if we can't load the CSV files
      const dummyData = [
        {
          "Program Name": "Sample Training Center",
          "County": "Alameda",
          "Program Type": "EMT",
          "Service To": "Public",
          "Accreditation Status": "Active",
          "Expiration Date": "2026-12-31",
          "Phone": "555-123-4567",
          "Address": "123 Main St",
          "City": "Oakland",
          "State": "CA",
          "Zip": "94612",
          "Website": "https://example.com",
          "Program Director": "John Doe",
          "zip": "94612",
          "Web Address": "https://example.com",
          "Approving Authority": "EMSA",
          "Program Contact": "John Doe"
        },
        {
          "Program Name": "LA Medical Training",
          "County": "Los Angeles",
          "Program Type": "Paramedic",
          "Service To": "Healthcare",
          "Accreditation Status": "Active",
          "Expiration Date": "2025-06-30",
          "Phone": "555-987-6543",
          "Address": "456 Oak Ave",
          "City": "Los Angeles",
          "State": "CA",
          "Zip": "90012",
          "Website": "https://lamedical.example.com",
          "Program Director": "Jane Smith",
          "zip": "90012",
          "Web Address": "https://lamedical.example.com",
          "Approving Authority": "LA County EMS",
          "Program Contact": "Jane Smith"
        },
        {
          "Program Name": "SF Emergency Training",
          "County": "San Francisco",
          "Program Type": "Advanced EMT",
          "Service To": "Emergency",
          "Accreditation Status": "Pending",
          "Expiration Date": "2026-03-15",
          "Phone": "555-246-8024",
          "Address": "789 Pine St",
          "City": "San Francisco",
          "State": "CA",
          "Zip": "94102",
          "Website": "https://sfemergency.example.com",
          "Program Director": "Robert Johnson",
          "zip": "94102",
          "Web Address": "https://sfemergency.example.com",
          "Approving Authority": "SF County EMS",
          "Program Contact": "Robert Johnson"
        }
      ];
      
      // Try to load Continuing Education data
      let ceData = [...dummyData];
      try {
        // console.log('Fetching Continuing Education data...');
        const ceResponse = await fetch(`${dataPath}/Continuing_Education.csv`);
        if (!ceResponse.ok) {
          console.warn(`Failed to load Continuing Education data: ${ceResponse.status}`);
        } else {
          const ceText = await ceResponse.text();
          const parsedData = parseCSV(ceText);
          if (parsedData && parsedData.length > 0) {
            ceData = parsedData;
            // console.log('Successfully loaded Continuing Education data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed Continuing Education data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading Continuing Education data:', err);
      }
      // Always set the data, even if it's just dummy data
      setContinuingEducation(ceData);
      
      // Try to load EMS Personnel data
      let emsData = [...dummyData];
      try {
        // console.log('Fetching EMS Personnel data...');
        const emsResponse = await fetch(`${dataPath}/EMS_Personnel.csv`);
        if (!emsResponse.ok) {
          console.warn(`Failed to load EMS Personnel data: ${emsResponse.status}`);
        } else {
          const emsText = await emsResponse.text();
          const parsedData = parseCSV(emsText);
          if (parsedData && parsedData.length > 0) {
            emsData = parsedData;
            // console.log('Successfully loaded EMS Personnel data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed EMS Personnel data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading EMS Personnel data:', err);
      }
      // Always set the data, even if it's just dummy data
      setEmsPersonnel(emsData);
      
      // Try to load Epinephrine Auto-Injector data
      let epiData = [...dummyData];
      try {
        // console.log('Fetching Epinephrine data...');
        const epiResponse = await fetch(`${dataPath}/Epinephrine_Auto_Injector.csv`);
        if (!epiResponse.ok) {
          console.warn(`Failed to load Epinephrine data: ${epiResponse.status}`);
        } else {
          const epiText = await epiResponse.text();
          const parsedData = parseCSV(epiText);
          if (parsedData && parsedData.length > 0) {
            epiData = parsedData;
            // console.log('Successfully loaded Epinephrine data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed Epinephrine data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading Epinephrine data:', err);
      }
      // Always set the data, even if it's just dummy data
      setEpinephrine(epiData);
      
      // Try to load Public Safety data
      let psData = [...dummyData];
      try {
        // console.log('Fetching Public Safety data...');
        const psResponse = await fetch(`${dataPath}/Public_Safety.csv`);
        if (!psResponse.ok) {
          console.warn(`Failed to load Public Safety data: ${psResponse.status}`);
        } else {
          const psText = await psResponse.text();
          const parsedData = parseCSV(psText);
          if (parsedData && parsedData.length > 0) {
            psData = parsedData;
            // console.log('Successfully loaded Public Safety data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed Public Safety data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading Public Safety data:', err);
      }
      // Always set the data, even if it's just dummy data
      setPublicSafety(psData);
      
      // Try to load Child Care Provider CPR and First Aid data
      let ccpData = [...dummyData];
      try {
        // console.log('Fetching Child Care Provider data...');
        const ccpResponse = await fetch(`${dataPath}/ChildCareProviderCPRandFirstAid.csv`);
        if (!ccpResponse.ok) {
          console.warn(`Failed to load Child Care Provider data: ${ccpResponse.status}`);
        } else {
          const ccpText = await ccpResponse.text();
          const parsedData = parseCSV(ccpText);
          if (parsedData && parsedData.length > 0) {
            ccpData = parsedData;
            // console.log('Successfully loaded Child Care Provider data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed Child Care Provider data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading Child Care Provider data:', err);
      }
      // Always set the data, even if it's just dummy data
      setChildCareProvider(ccpData);
      
      // Try to load Child Care Provider Preventive Health & Safety data
      let ccpPhsData = [...dummyData];
      try {
        // console.log('Fetching Child Care Provider Preventive Health & Safety data...');
        const ccpPhsResponse = await fetch(`${dataPath}/ChildCareProviderPreventiveHealthSafety.csv`);
        if (!ccpPhsResponse.ok) {
          console.warn(`Failed to load Child Care Provider Preventive Health & Safety data: ${ccpPhsResponse.status}`);
        } else {
          const ccpPhsText = await ccpPhsResponse.text();
          const parsedData = parseCSV(ccpPhsText);
          if (parsedData && parsedData.length > 0) {
            ccpPhsData = parsedData;
            // console.log('Successfully loaded Child Care Provider Preventive Health & Safety data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed Child Care Provider Preventive Health & Safety data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading Child Care Provider Preventive Health & Safety data:', err);
      }
      // Always set the data, even if it's just dummy data
      setChildCareProviderPreventiveHealth(ccpPhsData);
      
      // Try to load School Bus Driver First Aid data
      let sbdData = [...dummyData];
      try {
        // console.log('Fetching School Bus Driver First Aid data...');
        const sbdResponse = await fetch(`${dataPath}/SchoolBusDriverFirstAid.csv`);
        if (!sbdResponse.ok) {
          console.warn(`Failed to load School Bus Driver First Aid data: ${sbdResponse.status}`);
        } else {
          const sbdText = await sbdResponse.text();
          const parsedData = parseCSV(sbdText);
          if (parsedData && parsedData.length > 0) {
            sbdData = parsedData;
            // console.log('Successfully loaded School Bus Driver First Aid data:', parsedData.length, 'records');
          } else {
            // console.warn('Parsed School Bus Driver First Aid data was empty, using dummy data');
          }
        }
      } catch (err) {
        console.error('Error loading School Bus Driver First Aid data:', err);
      }
      // Always set the data, even if it's just dummy data
      setSchoolBusDriverFirstAid(sbdData);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Error loading data: ${err.message}`);
      setLoading(false);
    }
  }, [dataPath, parseCSV]);

  // Load data on component mount
  useEffect(() => {
    // console.log('EMSATrainingPrograms mounted, loading data...');
    // console.log('Current environment:', process.env.NODE_ENV);
    // console.log('Current public URL:', process.env.PUBLIC_URL);
    // console.log('Data path:', dataPath);
    loadData();
  }, [loadData]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    // console.log('Filter change in EMSATrainingPrograms:', newFilters);
    // Use JSON.stringify for deep comparison to prevent infinite update loops
    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      setFilters(newFilters);
    }
  }, [filters]);

  // Handle tab change
  const handleTabChange = useCallback((index) => {
    // console.log('Tab changed to:', index);
    setCurrentTab(index);
    // Reset filters when changing tabs
    setFilters({});
    
    // Navigate to the appropriate URL
    const pageIndex = currentPage - 1; // Convert to 0-based index
    if (TAB_ROUTES[pageIndex] && TAB_ROUTES[pageIndex][index]) {
      navigate(TAB_ROUTES[pageIndex][index]);
    }
  }, [currentPage, navigate]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setCurrentTab(0); // Reset to first tab when changing pages
    setFilters({});
    
    // Navigate to the first tab of the selected page
    const pageIndex = page - 1; // Convert to 0-based index
    if (TAB_ROUTES[pageIndex] && TAB_ROUTES[pageIndex][0]) {
      navigate(TAB_ROUTES[pageIndex][0]);
    }
  }, [navigate]);

  // Define columns and filter configurations for each dataset
  const dataConfigs = useMemo(() => [
    {
      name: 'Continuing Education',
      data: continuingEducation,
      columns: [
        { key: 'Program Name', header: 'Program Name', sortable: true },
        { key: 'County', header: 'County', sortable: true },
        { key: 'Expires', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Service To', header: 'Service to', sortable: true },
        { key: 'Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone', header: 'Phone', sortable: true, align: 'center', 
          render: (value) => formatPhoneNumber(value) },
        { key: 'WEBSITE', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'Program Name', label: 'Program Name', type: 'text' },
        { key: 'County', label: 'County', type: 'select',
          options: [...new Set(continuingEducation.map(item => item['County']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Service To', label: 'Service to', type: 'select',
          options: [...new Set(continuingEducation.map(item => item['Service To']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(continuingEducation.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'EMS Personnel',
      data: emsPersonnel,
      columns: [
        { key: 'Program Name', header: 'Program Name', sortable: true },
        { key: 'County', header: 'County', sortable: true },
        { key: 'Program Type', header: 'Program Type', sortable: true },
        { key: 'Service To', header: 'Service to', sortable: true },
        { key: 'Accreditation Status', header: 'Accreditation Status', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'Website', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'Program Name', label: 'Program Name', type: 'text' },
        { key: 'County', label: 'County', type: 'select',
          options: [...new Set(emsPersonnel.map(item => item['County']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Program Type', label: 'Program Type', type: 'select',
          options: [...new Set(emsPersonnel.map(item => item['Program Type']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Service To', label: 'Service to', type: 'select',
          options: [...new Set(emsPersonnel.map(item => item['Service To']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Accreditation Status', label: 'Accreditation Status', type: 'select',
          options: [...new Set(emsPersonnel.map(item => item['Accreditation Status']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(emsPersonnel.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'Epinephrine Auto-Injector',
      data: epinephrine,
      columns: [
        { key: 'Program Name', header: 'Program Name', sortable: true },
        { key: 'Program Director', header: 'Program Contact', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'Web Address', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'Program Name', label: 'Program Name', type: 'text' },
        { key: 'Program Director', label: 'Program Contact', type: 'select',
          options: [...new Set(epinephrine.map(item => item['Program Director']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(epinephrine.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'Public Safety First Aid',
      data: publicSafety,
      columns: [
        { key: 'Program Name', header: 'Program Name', sortable: true },
        { key: 'Approving Authority', header: 'Approving Authority', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Program Contact', header: 'Program Contact', sortable: true },
        { key: 'Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'Web Address', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'Program Name', label: 'Program Name', type: 'text' },
        { key: 'Approving Authority', label: 'Approving Authority', type: 'select',
          options: [...new Set(publicSafety.map(item => item['Approving Authority']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Program Contact', label: 'Program Contact', type: 'select',
          options: [...new Set(publicSafety.map(item => item['Program Contact']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(publicSafety.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'Child Care Provider CPR and First Aid',
      data: childCareProvider,
      columns: [
        { key: 'EPI Approved', header: 'EPI Approved', sortable: true, align: 'center',
          render: (value) => value === '1' ? 'Yes' : 'No' },
        { key: 'PROGRAM_NAME', header: 'Program Name', sortable: true },
        { key: 'Counties Served', header: 'Counties Served', sortable: true },
        { key: 'LANGUAGES', header: 'Languages', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Director', header: 'Director', sortable: true },
        { key: 'Co-Director', header: 'Co-Director', sortable: true },
        { key: 'Program Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone Number', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'WEBSITE', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'EPI Approved', label: 'EPI Approved', type: 'select',
          options: [
            { value: '1', label: 'Yes' },
            { value: '0', label: 'No' }
          ]
        },
        { key: 'PROGRAM_NAME', label: 'Program Name', type: 'text' },
        { key: 'Counties Served', label: 'Counties Served', type: 'text' },
        { key: 'LANGUAGES', label: 'Languages', type: 'select',
          options: [...new Set(childCareProvider.flatMap(item => 
            (item['LANGUAGES'] || '').split(',').map(lang => lang.trim())
          ))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Director', label: 'Director', type: 'select',
          options: [...new Set(childCareProvider.map(item => item['Director']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(childCareProvider.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'Child Care Provider Preventive Health & Safety',
      data: childCareProviderPreventiveHealth,
      columns: [
        { key: 'PROGRAM_NAME', header: 'Program Name', sortable: true },
        { key: 'Counties Served', header: 'Counties Served', sortable: true },
        { key: 'LANGUAGES', header: 'Languages', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Director', header: 'Director', sortable: true },
        { key: 'Co-Director', header: 'Co-Director', sortable: true },
        { key: 'Program Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone Number', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'WEBSITE', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'PROGRAM_NAME', label: 'Program Name', type: 'text' },
        { key: 'Counties Served', label: 'Counties Served', type: 'text' },
        { key: 'LANGUAGES', label: 'Languages', type: 'select',
          options: [...new Set(childCareProviderPreventiveHealth.flatMap(item => 
            (item['LANGUAGES'] || '').split(',').map(lang => lang.trim())
          ))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Director', label: 'Director', type: 'select',
          options: [...new Set(childCareProviderPreventiveHealth.map(item => item['Director']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(childCareProviderPreventiveHealth.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    },
    {
      name: 'School Bus Driver First Aid',
      data: schoolBusDriverFirstAid,
      columns: [
        { key: 'PROGRAM_NAME', header: 'Program Name', sortable: true },
        { key: 'Counties Served', header: 'Counties Served', sortable: true },
        { key: 'LANGUAGES', header: 'Languages', sortable: true },
        { key: 'Expiration Date', header: 'Expiration', sortable: true, type: 'date', align: 'center' },
        { key: 'Director', header: 'Director', sortable: true },
        { key: 'Co-Director', header: 'Co-Director', sortable: true },
        { key: 'Program Address', header: 'Address', sortable: true },
        { key: 'City, State, Zip', header: 'City, State, Zip', sortable: true },
        { key: 'Phone Number', header: 'Phone', sortable: true, align: 'center',
          render: (value) => formatPhoneNumber(value) },
        { key: 'WEBSITE', header: 'Website', sortable: true,
          render: (value) => value && value.trim() ? (
            <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="external-link">
              Visit Website<span className="sr-only"> (opens in new tab)</span><span className="external-icon" aria-hidden="true">↗</span>
            </a>
          ) : '' }
      ],
      filterConfig: [
        { key: 'PROGRAM_NAME', label: 'Program Name', type: 'text' },
        { key: 'Counties Served', label: 'Counties Served', type: 'text' },
        { key: 'LANGUAGES', label: 'Languages', type: 'select',
          options: [...new Set(schoolBusDriverFirstAid.flatMap(item => 
            (item['LANGUAGES'] || '').split(',').map(lang => lang.trim())
          ))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'Director', label: 'Director', type: 'select',
          options: [...new Set(schoolBusDriverFirstAid.map(item => item['Director']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        },
        { key: 'City', label: 'City', type: 'text' },
        { key: 'State', label: 'State', type: 'select',
          options: [...new Set(schoolBusDriverFirstAid.map(item => item['State']))]
            .filter(Boolean)
            .sort()
            .map(value => ({ value, label: value }))
        }
      ]
    }
  ], [continuingEducation, emsPersonnel, epinephrine, publicSafety, childCareProvider, childCareProviderPreventiveHealth, schoolBusDriverFirstAid]);

  // Split tabs into pages - Page 1: EMSA Training Programs, Page 2: Child Care Training Information
  // Public Safety on Page 1, Epinephrine on Page 2
    const page1Tabs = useMemo(() => [
    dataConfigs[0], // Continuing Education
    dataConfigs[1], // EMS Personnel
    dataConfigs[3]  // Public Safety
  ], [dataConfigs]);
  
  const page2Tabs = useMemo(() => [
    dataConfigs[2], // Epinephrine Auto-Injector
    dataConfigs[4], // Child Care Provider CPR and First Aid
    dataConfigs[5], // Child Care Provider Preventive Health & Safety
    dataConfigs[6]  // School Bus Driver First Aid
  ], [dataConfigs]);

  // Get current page tabs
  const currentTabs = useMemo(() => {
    return currentPage === 1 ? page1Tabs : page2Tabs;
  }, [currentPage, page1Tabs, page2Tabs]);

  // Prepare tabs for EMSATabsContainer
  const tabs = useMemo(() => currentTabs.map((config) => {
    // Calculate filtered count for this tab's data
    const filteredCount = config.data.filter(item => {
      if (!filters || Object.keys(filters).length === 0) {
        return true; // No filters applied, return all items
      }
      return Object.entries(filters).every(([key, value]) => {
        // Skip empty filter values
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }

        const itemValue = item[key];

        // Handle different filter types
        if (Array.isArray(value)) {
          // Multi-select filter
          return value.includes(itemValue);
        } else if (typeof value === 'string') {
          // Text filter - case insensitive search
          return itemValue &&
            itemValue.toString().toLowerCase().includes(value.toLowerCase());
        }

        return true;
      });
    }).length;

    return {
      label: `${config.name}`,
      content: (
        <>
          <div className="program-info">
            <h2>{config.name}</h2>
            <p>Total Programs: {filteredCount}</p>
          </div>

          <section aria-label="Filter training programs">
            <FilterPanel
              filterConfig={config.filterConfig}
              initialFilters={{}}
              onFilterChange={handleFilterChange}
            />
          </section>

          <section aria-label={`${config.name} programs table`}>
            <EnhancedDataTable
              data={config.data}
              columns={config.columns}
              filters={filters}
              itemsPerPage={15}
            />
          </section>
        </>
      )
    };
  }), [currentTabs, filters, handleFilterChange]);

  // If loading, show loading message
  if (loading) {
    return <div className="loading">Loading State of California Training Programs data...</div>;
  }

  // If error, show error message
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="emsa-training-programs">
      {/* Skip Navigation Link */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Live Region for Announcements */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        id="announcements"
      >
        {tabs.length > 0 && tabs[0] && `Showing training programs`}
      </div>

      <div className="container">
        <h1>State of California Training Programs</h1>
        <p className="lead">
          Browse through various Emergency Medical Services Authority (EMSA) training programs
          available in California. Use the filters below to find specific programs.
        </p>
      </div>

      <main id="main-content" role="main">
      <div className="full-width-section">
        {/* Page Navigation */}
        <div className="page-navigation" role="group" aria-label="Training program pages">
          <button
            className={`page-btn ${currentPage === 1 ? 'active' : ''}`}
            onClick={() => handlePageChange(1)}
            aria-pressed={currentPage === 1}
            aria-current={currentPage === 1 ? 'page' : undefined}
          >
            EMS Training Programs
          </button>
          <button
            className={`page-btn ${currentPage === 2 ? 'active' : ''}`}
            onClick={() => handlePageChange(2)}
            aria-pressed={currentPage === 2}
            aria-current={currentPage === 2 ? 'page' : undefined}
          >
            General Training Programs
          </button>
        </div>

        {/* Contact Provider Note */}
        <div className="contact-provider-note">
          <p>Please contact the provider for EMSA-approved instructors in your area.</p>
        </div>

        <EMSATabsContainer
          tabs={tabs}
          onTabChange={handleTabChange}
          activeTab={currentTab}
        />

        {/* Expired Programs Notice */}
        <div className="expired-notice" role="note" aria-label="Information about program display">
          <p>Note: Programs with past expiration dates are not displayed.</p>
        </div>
      </div>
      </main>
    </div>
  );
};

export default EMSATrainingPrograms;
